import PubNub from 'pubnub';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { EMOJI_TYPES } from 'client/types';

import { ParsedPubnubMessage, PubnubMsgsItem, RaiseHandMessage } from './types';
import {
  checkUserRaiseHandMsgBuilder,
  deleteMessageBuilder,
  getActiveUsersBuilder,
  getAllowedToSpeakListBuilder,
  getAllUsersMetadataBuilder,
  getBlockedUsersBuilder,
  getBlockedUsersCountBuilder,
  getRaiseHandMsgsBuilder,
  getRaiseHandMsgsCountBuilder,
  getReactionsMsgsBuilder,
  getReactionsMsgsCountBuilder,
  getSystemChannelMetadataBuilder,
  getUserMetadataBuilder,
  publishMessageBuilder,
} from './extra-reducers';
import { PUBNUB_SUBSCRIBE_KEY, PUBNUB_PUBLISH_KEY } from './constants';
import * as actions from './actions';
import { UserCustom } from './types';

export type PubnubState = {
  pubnub: PubNub | null;
  isConnected: boolean;
  occupancy: {
    anonymous: number;
    signedIn: number;
  };
  user: { blocked: string; alreadySpeakTotal: string | number };
  spokeUsersMetaData: {
    nextPage: string;
    totalCount: number;
    usersData: Array<PubNub.UUIDMetadataObject<UserCustom>>;
  };
  reactions: {
    messages: Record<EMOJI_TYPES, Array<PubnubMsgsItem>>;
    counts: Record<EMOJI_TYPES, number>;
  };
  blocked: {
    messages: Array<PubnubMsgsItem>;
    total: number;
  };
  activeSpeakers: Array<string>;
  listenersAllowedToSpeak: Array<RaiseHandMessage>;
  raiseHand: {
    messages: Array<PubnubMsgsItem>;
    total: number;
  };
  currentRaiseHandMsgTT: string | null;
  currentReactionMsgTT: string | null;
  isInitialized: boolean;
  isBusy: boolean;
};

export const initialState: PubnubState = {
  pubnub: null,
  occupancy: {
    anonymous: 0,
    signedIn: 0,
  },
  reactions: {
    messages: {
      [EMOJI_TYPES.ANGRY]: [],
      [EMOJI_TYPES.CRY]: [],
      [EMOJI_TYPES.HEART]: [],
      [EMOJI_TYPES.SMILE]: [],
      [EMOJI_TYPES.LIKE]: [],
      [EMOJI_TYPES.WONDER]: [],
    },
    counts: {
      [EMOJI_TYPES.ANGRY]: 0,
      [EMOJI_TYPES.CRY]: 0,
      [EMOJI_TYPES.HEART]: 0,
      [EMOJI_TYPES.SMILE]: 0,
      [EMOJI_TYPES.LIKE]: 0,
      [EMOJI_TYPES.WONDER]: 0,
    },
  },
  raiseHand: {
    messages: [],
    total: 0,
  },
  blocked: {
    messages: [],
    total: 0,
  },
  user: { blocked: '', alreadySpeakTotal: '0' },
  spokeUsersMetaData: {
    nextPage: '',
    totalCount: 0,
    usersData: [],
  },
  activeSpeakers: [],
  isConnected: false,
  listenersAllowedToSpeak: [],
  currentRaiseHandMsgTT: null,
  currentReactionMsgTT: null,
  isInitialized: false,
  isBusy: false,
};

export const pubnub = createSlice({
  name: 'pubnub',
  initialState,
  reducers: {
    init: (state, action: PayloadAction<actions.InitType>) => {
      state.pubnub = new PubNub({
        subscribeKey: PUBNUB_SUBSCRIBE_KEY,
        publishKey: PUBNUB_PUBLISH_KEY,
        userId: action.payload.uuid,
        heartbeatInterval: 30,
        presenceTimeout: 60,
      });

      state.currentRaiseHandMsgTT = action.payload.raiseHandMessageTT;
      state.currentReactionMsgTT = action.payload.reactionMsgTT;
      state.isInitialized = true;
    },
    updateActiveSpeakers: (state, action: PayloadAction<Array<string>>) => {
      if (action.payload) state.activeSpeakers = action.payload;
    },

    updateUserMetadata: (state, action: PayloadAction<actions.UpdateUserMetadata>) => {
      if (action.payload.blocked !== undefined) state.user.blocked = action.payload.blocked;
      if (action.payload.alreadySpeakTotal !== undefined)
        state.user.alreadySpeakTotal = action.payload.alreadySpeakTotal;
    },

    removeReactionMsgTT: (state) => {
      state.currentReactionMsgTT = null;
    },

    resetPubnubUserInfo: (state) => {
      state.user.blocked = '';
      state.user.alreadySpeakTotal = '0';
    },

    removeReaction: (state, action: PayloadAction<PubNub.MessageActionEvent>) => {
      const emojiType = action.payload.channel.split('.')?.pop() || EMOJI_TYPES.SMILE;
      const currentEmojiTypeMessages = state.reactions.messages[emojiType as EMOJI_TYPES];
      const currentEmojiTypeCounts = state.reactions.counts[emojiType as EMOJI_TYPES];

      state.reactions.messages[emojiType as EMOJI_TYPES] = _.filter(
        currentEmojiTypeMessages,
        (m) => m.timetoken !== action.payload.data.messageTimetoken,
      );

      state.reactions.counts[emojiType as EMOJI_TYPES] = currentEmojiTypeCounts - 1;
    },

    addReaction: (state, action: PayloadAction<PubNub.MessageEvent>) => {
      const emojiType = action.payload.channel.split('.')?.pop() || EMOJI_TYPES.SMILE;
      const currentEmojiTypeMessages = state.reactions.messages[emojiType as EMOJI_TYPES];
      const currentEmojiTypeCounts = state.reactions.counts[emojiType as EMOJI_TYPES];

      state.reactions.messages[emojiType as EMOJI_TYPES] = [action.payload, ...currentEmojiTypeMessages];
      state.reactions.counts[emojiType as EMOJI_TYPES] = currentEmojiTypeCounts + 1;
    },

    updateChannelCustomFields: (
      state,
      action: PayloadAction<PubNub.ObjectsEvent<PubNub.ObjectCustom, PubNub.ObjectCustom, PubNub.ObjectCustom>>,
    ) => {
      const newListenersAllowedToSpeak = JSON.parse(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        _.get(action.payload, 'message.data.custom.listenersAllowedToSpeak', '[]') || '[]',
      );

      if (!_.isEqual(newListenersAllowedToSpeak, state.listenersAllowedToSpeak)) {
        state.listenersAllowedToSpeak = newListenersAllowedToSpeak;
      }
    },

    addRaiseHand: (state, action: PayloadAction<Array<PubNub.MessageEvent>>) => {
      const newMessages: Array<PubnubMsgsItem> = [];

      _.forEach(action.payload, (m) => {
        if (_.findIndex(state.raiseHand.messages, { timetoken: m.timetoken }) < 0) {
          newMessages.push({
            ..._.pick(m, ['channel', 'timetoken', 'message', 'actions']),
            uuid: m.publisher,
            messageType: undefined,
          } as PubnubMsgsItem);
        }
      });

      state.raiseHand.messages = [...newMessages, ...state.raiseHand.messages];
      state.raiseHand.total += newMessages.length;
    },

    removeRaiseHand: (state, action: PayloadAction<string>) => {
      const oldLength = state.raiseHand.messages.length;

      state.raiseHand.messages = _.filter(state.raiseHand.messages, (msg) => msg.timetoken !== action.payload);

      if (oldLength !== state.raiseHand.messages.length) {
        state.raiseHand.total -= 1;
      }

      if (state.currentRaiseHandMsgTT === action.payload) {
        state.currentRaiseHandMsgTT = null;
      }
    },

    updateRHMessage: (state, action: PayloadAction<PubNub.MessageActionEvent>) => {
      const { data } = action.payload;
      const targetMessageIndex = _.findIndex(state.raiseHand.messages, { timetoken: data.messageTimetoken });
      const rhMessage = state.raiseHand.messages[targetMessageIndex];
      const newData: ParsedPubnubMessage = JSON.parse(data.value || '{}');

      if (rhMessage) {
        const messageData = JSON.parse(rhMessage.message);
        const actionKey = JSON.stringify({
          ...messageData,
          alreadySpeakTotal: `${newData.alreadySpeakTotal || '0'}`,
        });

        _.set(rhMessage, 'message', actionKey);
        _.set(rhMessage, 'actions', {
          update: { [`${actionKey}`]: [{ uuid: data.uuid, actionTimetoken: data.actionTimetoken }] },
        });

        state.raiseHand.messages.splice(targetMessageIndex, targetMessageIndex + 1, rhMessage);
      }
    },

    addUserToBlocked: (state, action: PayloadAction<PubNub.MessageEvent>) => {
      if (_.findIndex(state.blocked.messages, { timetoken: action.payload.timetoken }) < 0) {
        const data = {
          ..._.pick(action.payload, ['channel', 'timetoken', 'message']),
          uuid: action.payload.publisher,
          messageType: undefined,
        };
        state.blocked.messages.unshift(data as PubnubMsgsItem);
        state.blocked.total = state.blocked.total + 1;
      }
    },

    removeUserFromBlocked: (state, action: PayloadAction<string>) => {
      if (_.findIndex(state.blocked.messages, { timetoken: action.payload }) >= 0) {
        state.blocked.messages = _.filter(state.blocked.messages, (msg) => msg.timetoken !== action.payload);
        state.blocked.total = state.blocked.total - 1;
      }
    },

    update: (state, action: PayloadAction<Partial<Omit<PubnubState, 'pubnub'>>>) => {
      if (action.payload.occupancy) state.occupancy = action.payload.occupancy;
      if (action.payload.isInitialized) state.isInitialized = action.payload.isInitialized;
      if (action.payload.isConnected) state.isConnected = action.payload.isConnected;
      if (action.payload.isBusy) state.isBusy = action.payload.isBusy;
    },
    destroy: (state) => {
      state.pubnub?.unsubscribeAll();
      state.pubnub?.stop();

      state.reactions = initialState.reactions;
      state.pubnub = initialState.pubnub;
      state.occupancy = initialState.occupancy;
      state.raiseHand = initialState.raiseHand;
      state.blocked = initialState.blocked;
      state.user = initialState.user;
      state.spokeUsersMetaData = initialState.spokeUsersMetaData;
      state.activeSpeakers = initialState.activeSpeakers;
      state.isConnected = initialState.isConnected;
      state.listenersAllowedToSpeak = initialState.listenersAllowedToSpeak;
      state.currentRaiseHandMsgTT = initialState.currentRaiseHandMsgTT;
      state.currentReactionMsgTT = initialState.currentReactionMsgTT;
      state.isInitialized = initialState.isInitialized;
      state.isBusy = initialState.isBusy;
    },
  },
  extraReducers: (builder) => {
    // ACTION: getActiveUsers ==========================================================
    getActiveUsersBuilder(builder);

    // ACTION: getRaiseHandMessages ====================================================
    getRaiseHandMsgsBuilder(builder);

    // ACTION: publishMessage ==========================================================
    publishMessageBuilder(builder);

    // ACTION: deleteMessage ===========================================================
    deleteMessageBuilder(builder);

    // ACTION: getAllowedToSpeakList ===================================================
    getAllowedToSpeakListBuilder(builder);

    // ACTION: getReactionsMessagesCount ===============================================
    getReactionsMsgsCountBuilder(builder);

    // ACTION: getRaiseHandMessagesCount ===============================================
    getRaiseHandMsgsCountBuilder(builder);

    // ACTION: getReactionsMessages ====================================================
    getReactionsMsgsBuilder(builder);

    // ACTION: getBlockedUsers =========================================================
    getBlockedUsersBuilder(builder);

    // ACTION: getBlockedUsersCount ====================================================
    getBlockedUsersCountBuilder(builder);

    // ACTION: getUserMetadata =========================================================
    getUserMetadataBuilder(builder);

    // ACTION: getAllUsersMetadata =====================================================
    getAllUsersMetadataBuilder(builder);

    // ACTION: checkUserRaiseHandMsg ===================================================
    checkUserRaiseHandMsgBuilder(builder);

    // ACTION: getSystemChannelMetadata ================================================
    getSystemChannelMetadataBuilder(builder);
  },
});

export const {
  addRaiseHand,
  addReaction,
  addUserToBlocked,

  init,
  destroy,

  removeRaiseHand,
  removeReaction,
  removeReactionMsgTT,
  removeUserFromBlocked,
  resetPubnubUserInfo,

  update,
  updateActiveSpeakers,
  updateChannelCustomFields,
  updateRHMessage,
  updateUserMetadata,
} = pubnub.actions;
export default pubnub.reducer;
